import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// 20 adımlık kapsamlı tour sistemi (header tour'ları dashboard'a dahil edildi)
export type TourStep = 
  | 'dashboard'           // 1. Kontrol Paneli (header tour'ları dahil)
  | 'employees'           // 2. Çalışan listesi  
  | 'departments'         // 3. Departmanlar
  | 'branches'            // 4. Şubeler
  | 'attendance-report'   // 5. Giriş Çıkış Raporu
  | 'shifts'              // 6. Vardiyalar (+ modal tour)
  | 'leaves'              // 7. İzinler
  | 'tickets'             // 8. Talepler
  | 'forgot-checkout'     // 9. Çıkış Yapmayanlar
  | 'forgot-checkin'      // 10. Giriş Yapmayanlar
  | 'assets'              // 11. Zimmet Yönetimi
  | 'meals'               // 12. Yemek Yönetimi
  | 'employee-report'     // 13. Çalışan Raporu
  | 'timesheet-report'   // 18. Puantaj Raporu (timesheet-report)
  | 'shift-report'        // 14. Performans Raporu
  | 'leave-report'        // 15. İzin Raporu
  | 'break-report'        // 16. Mola Raporu
  | 'documents'           // 17. Belgeler
  | 'company-settings'    // 18. Şirket Ayarları (+ tab tours)
  | 'notifications'       // 19. Bildirim
  | 'tour-button'         // 20. Header tour butonu
  | 'completed'           // Tour tamamlandı

interface TourState {
  isFirstLogin: boolean;
  tourCompleted: boolean;
  isOpen: boolean;
  currentStep: TourStep;
  stepIndex: number;
  isNavigating: boolean;
  modalTourActive: boolean; // Modal içi tour için
  modalTourStep: number;    // Modal içindeki adım
}

// 20 adımlık tour sırası (header tour'ları dashboard'a dahil edildi)
const tourSteps: TourStep[] = [
  'dashboard',
  'employees',
  'departments',
  'branches',
  'attendance-report',
  'shifts',
  'leaves',
  'tickets',
  'forgot-checkout',
  'forgot-checkin',
  'assets',
  'meals',
  'employee-report',
  'shift-report',
  'leave-report',
  'break-report',
  'documents',
  'timesheet-report',
  'company-settings',
  'notifications',
  'tour-button',
  'completed'
];

const initialState: TourState = {
  isFirstLogin: false, // Artık otomatik başlamayacak
  tourCompleted: false, // Her zaman manuel başlatılabilir
  isOpen: false,
  currentStep: 'dashboard',
  stepIndex: 0,
  isNavigating: false,
  modalTourActive: false,
  modalTourStep: 0,
};

const tourSlice = createSlice({
  name: 'tour',
  initialState,
  reducers: {
    startTour: (state) => {
      state.isOpen = true;
      state.currentStep = 'dashboard';
      state.stepIndex = 0;
      state.isNavigating = false;
      state.modalTourActive = false;
      state.modalTourStep = 0;
    },
    openTour: (state) => {
      state.isOpen = true;
    },
    closeTour: (state) => {
      state.isOpen = false;
      state.currentStep = 'dashboard';
      state.stepIndex = 0;
      state.isNavigating = false;
      state.modalTourActive = false;
      state.modalTourStep = 0;
      localStorage.setItem('hasSeenAutoTour', 'true'); // Turu 'X' ile kapatmak 'görüldü' sayılacak (modal onayı sonrası)
    },
    nextTourStep: (state) => {
      
      if (state.stepIndex < tourSteps.length - 1) {
        state.stepIndex += 1;
        state.currentStep = tourSteps[state.stepIndex];
        state.isNavigating = true;
        state.isOpen = true; // Tour'u açık tut
        
      } else {
      }
    },
    prevTourStep: (state) => {
      if (state.stepIndex > 0) {
        state.stepIndex -= 1;
        state.currentStep = tourSteps[state.stepIndex];
        state.isNavigating = true;
        state.isOpen = true; // Tour'u açık tut
      }
    },
    setTourStep: (state, action: PayloadAction<TourStep>) => {
      const stepIndex = tourSteps.indexOf(action.payload);
      if (stepIndex !== -1) {
        state.currentStep = action.payload;
        state.stepIndex = stepIndex;
        state.isNavigating = false;
      }
    },
    setNavigationComplete: (state) => {
      state.isNavigating = false;
    },
    // Force sync Redux state
    forceUpdateTourState: (state, action: PayloadAction<{ currentStep: TourStep; stepIndex: number }>) => {
      state.currentStep = action.payload.currentStep;
      state.stepIndex = action.payload.stepIndex;
      state.isOpen = true;
      state.isNavigating = false;
    },
    // Modal tour actions
    startModalTour: (state) => {
      state.modalTourActive = true;
      state.modalTourStep = 0;
    },
    nextModalStep: (state) => {
      state.modalTourStep += 1;
    },
    prevModalStep: (state) => {
      if (state.modalTourStep > 0) {
        state.modalTourStep -= 1;
      }
    },
    closeModalTour: (state) => {
      state.modalTourActive = false;
      state.modalTourStep = 0;
    },
    completeTour: (state) => {
      state.tourCompleted = true;
      state.isFirstLogin = false;
      state.isOpen = false;
      state.currentStep = 'dashboard';
      state.stepIndex = 0;
      state.isNavigating = false;
      state.modalTourActive = false;
      state.modalTourStep = 0;
      localStorage.setItem('hasSeenAutoTour', 'true'); // Turu gördü olarak işaretle
      // LocalStorage'a kaydetmeye gerek yok - her zaman manuel başlatılabilir
    },
    resetTour: (state) => {
      state.tourCompleted = false;
      state.isFirstLogin = true;
      state.isOpen = false;
      state.currentStep = 'dashboard';
      state.stepIndex = 0;
      state.isNavigating = false;
      state.modalTourActive = false;
      state.modalTourStep = 0;
      localStorage.removeItem('tourCompleted');
      localStorage.removeItem('isFirstLogin');
    }
  }
});

export const {
  startTour,
  openTour,
  closeTour,
  nextTourStep,
  prevTourStep,
  setTourStep,
  setNavigationComplete,
  forceUpdateTourState,
  startModalTour,
  nextModalStep,
  prevModalStep,
  closeModalTour,
  completeTour,
  resetTour
} = tourSlice.actions;

export default tourSlice.reducer;